import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronDown, 
  User, 
  LogOut, 
  Settings, 
  TrendingUp, 
  Wallet, 
  BarChart2, 
  Repeat, 
  Bot, 
  Calendar, 
  Award, 
  Home,
  Landmark,
  TrendingDown,
  Briefcase,
  ChevronUp,
  Search,
  DollarSign,
  Bitcoin,
  CreditCard,
  BarChart,
  Gem,
  Euro,
  MoreHorizontal,
  Target,
  Zap,
  BarChart3,
  Layers
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { TradingMode, UserStatus } from '../App';

// Custom SVG Icons for Investment Tiers
const ShrimpIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
    <path d="M15.4091492,18.6986036 L15.5,18.5 C14.6355086,16.4828534 12.4348726,15.3912818 10.3058074,15.9235481 L10,16 C10,17.3807119 11.1192881,18.5 12.5,18.5 C11.1745166,18.5 10.0899613,19.5315359 10.0053177,20.8356243 L10,21 L10.3058074,21.0764519 C12.3639038,21.5909759 14.488879,20.5881138 15.4091492,18.6986036 L15.4091492,18.6986036 Z M15.5,18.5 L16.0876894,15.2677081 C16.3179456,14.0012994 15.5065195,12.7792266 14.25,12.5 C12.7524581,12.1672129 11.2843177,11.7137271 9.85996697,11.1439868 L9.5,11 C7.99008611,10.3960344 7,8.93364462 7,7.3074176 L7,7 L7,7 C5.8954305,7 5,6.1045695 5,5 L5,4 L5,4 L5,5 C5,6.1045695 5.8954305,7 7,7 L10,7 L5,7 C3.8954305,7 3,6.1045695 3,5 L3,3 L3,3 L3,5 C3,6.1045695 3.8954305,7 5,7 L10,7 L10,7 L13.0377855,7 C14.6611857,7 16.2665182,7.34067476 17.75,8 L18.1161292,8.1627241 C19.3050993,8.69115526 20.1844343,9.73773708 20.5,11 C20.822649,12.290596 20.6729809,13.6540381 20.0780456,14.8439089 L20,15 C19.3543116,16.2913768 18.2717937,17.3120884 16.9447292,17.8808303 L15.5,18.5 L15.5,18.5 Z M20.6503318,12.7744367 L20.539997,12.9515358 C19.7452448,14.1659903 18.3449036,14.8965565 16.8438806,14.8027425 L16.1253897,14.7573974 M20.0306873,9.86186454 L18.2226934,11.468717 L18.0277613,11.6323951 C17.033486,12.4205412 15.7520231,12.7531999 14.4999327,12.5481943 M16.8048928,7.6285803 L13.9923496,10.1887731 L13.8143356,10.3419788 C12.6651871,11.2777778 11.1129282,11.5562543 9.70980266,11.0776843 L9.88380266,11.1319241" />
    <line x1="11" y1="9" x2="11.1" y2="9" strokeWidth="1.5" />
  </svg>
);

const CrabIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 460 460" className={className} fill="currentColor">
    <path d="M101.639,247.568c3.818,0,7.076-2.971,7.463-6.76c0.304-2.977-1.291-5.94-3.929-7.345c-2.978-1.585-6.76-0.959-9.05,1.53C91.796,239.698,95.25,247.568,101.639,247.568z"/>
    <path d="M457.438,207.397l-2.806-2.806c-23.437-23.437-57.599-29.741-86.828-18.953c3.354-9.05,5.119-18.715,5.119-28.654c0-21.99-8.563-42.665-24.112-58.214l-2.806-2.806c-1.407-1.407-3.315-2.197-5.305-2.197s-3.896,0.791-5.304,2.198l-2.804,2.805c-28.948,28.949-31.778,74.265-8.512,106.427H26.552c-7.624,0-14.885,3.284-19.92,9.009c-5.054,5.746-7.392,13.401-6.414,21.002c11.167,86.768,85.657,152.199,173.271,152.199c7.843,0,15.726-0.532,23.492-1.575c13.19,13.441,31.542,21.8,51.816,21.8c4.143,0,7.5-3.358,7.5-7.5c0-10.639-2.266-20.896-6.524-30.22c55.223-26.797,92.111-80.396,97.688-141.128c14.486,10.728,31.719,16.1,48.955,16.1c21.083,0,42.165-8.025,58.215-24.075l2.806-2.806C460.367,215.075,460.367,210.326,457.438,207.397z"/>
  </svg>
);

const OctopusIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" className={className} fill="currentColor">
    <path d="M256,32C149.961,32,64,117.961,64,224c0,53.019,21.5,101.056,56.235,135.791C154.944,394.5,202.981,416,256,416s101.056-21.5,135.765-56.209C426.5,325.056,448,277.019,448,224C448,117.961,362.039,32,256,32z M256,384c-88.365,0-160-71.635-160-160S167.635,64,256,64s160,71.635,160,160S344.365,384,256,384z M208,176c-8.837,0-16,7.163-16,16s7.163,16,16,16s16-7.163,16-16S216.837,176,208,176z M304,176c-8.837,0-16,7.163-16,16s7.163,16,16,16s16-7.163,16-16S312.837,176,304,176z M256,240c-17.673,0-32,14.327-32,32s14.327,32,32,32s32-14.327,32-32S273.673,240,256,240z"/>
  </svg>
);

const DolphinIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 480 480" className={className} fill="currentColor">
    <path d="M184.999,187.968c-25.528,0-46.297,20.762-46.297,46.282c0,25.528,20.769,46.297,46.297,46.297c25.52,0,46.282-20.769,46.282-46.297C231.281,208.73,210.519,187.968,184.999,187.968z M184.999,260.547c-14.5,0-26.297-11.797-26.297-26.297c0-14.492,11.797-26.282,26.297-26.282c14.492,0,26.282,11.79,26.282,26.282C211.281,248.75,199.491,260.547,184.999,260.547z"/>
    <path d="M295.537,187.968c-25.52,0-46.282,20.762-46.282,46.282c0,25.528,20.762,46.297,46.282,46.297c25.528,0,46.297-20.769,46.297-46.297C341.833,208.73,321.065,187.968,295.537,187.968z M295.537,260.547c-14.492,0-26.282-11.797-26.282-26.297c0-14.492,11.79-26.282,26.282-26.282c14.5,0,26.297,11.79,26.297,26.282C321.833,248.75,310.037,260.547,295.537,260.547z"/>
    <path d="M407.573,302.507c-5.476-0.693-10.479,3.197-11.167,8.678c-0.687,5.479,3.198,10.479,8.678,11.166c25.517,3.201,43.683,26.568,40.497,52.087c-2.015,16.108-10.184,30.468-23.002,40.435c-12.82,9.967-28.753,14.342-44.87,12.322c-19.419-2.423-38.181-15.953-51.474-37.124c-12.459-19.841-18.553-43.868-16.547-64.772c60.917-25.639,103.796-85.938,103.796-156.066C413.484,75.917,337.566,0,244.25,0C150.935,0,75.018,75.917,75.018,169.232c0,67.977,40.292,126.712,98.244,153.603c0.008,0.079,0.007,0.158,0.018,0.238c2.637,21.081-4.011,46.046-17.784,66.782c-14.129,21.273-33.522,34.883-53.214,37.34c-16.109,2.02-32.044-2.356-44.863-12.323c-12.818-9.967-20.987-24.326-23.002-40.433c-3.187-25.522,14.98-48.888,40.497-52.089c5.48-0.686,9.365-5.686,8.678-11.166c-0.687-5.48-5.689-9.372-11.167-8.678c-36.454,4.572-62.406,37.953-57.853,74.413c2.679,21.409,13.536,40.495,30.571,53.74c14.321,11.136,31.564,17.021,49.412,17.021c3.387,0,6.796-0.212,10.21-0.641c25.473-3.177,50.036-19.987,67.392-46.119c14.223-21.414,22.001-46.935,21.663-70.129c5.588,1.748,11.302,3.208,17.121,4.374c-2.091,22.454-15.063,122.08-80.359,124.843c-5.518,0.233-9.802,4.897-9.568,10.414c0.227,5.374,4.656,9.577,9.984,9.577c0.143,0,0.286-0.003,0.43-0.009c38.414-1.626,67.919-28.371,85.326-77.346c9.542-26.849,12.925-53.106,14.029-64.716c4.445,0.352,8.937,0.536,13.47,0.536c1.843,0,3.676-0.04,5.505-0.099c1.077,11.859,4.332,37.76,13.564,64.224c17.097,49.009,46.447,75.773,84.877,77.4c0.144,0.006,0.287,0.009,0.43,0.009c5.328,0,9.756-4.203,9.984-9.577c0.233-5.518-4.05-10.181-9.568-10.414c-63.879-2.704-77.02-98.675-79.347-123.455c6.673-1.011,13.217-2.42,19.613-4.189c-0.155,22.852,6.984,47.632,19.988,68.341c16.501,26.277,40.534,43.166,65.93,46.333c3.416,0.429,6.827,0.641,10.215,0.641c17.85,0,35.093-5.885,49.413-17.019c17.036-13.246,27.893-32.332,30.571-53.743C469.979,340.46,444.026,307.079,407.573,302.507z"/>
  </svg>
);

const SharkIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" className={className} fill="currentColor">
    <path d="M329.939,307.833c-5.498-1.288-10.996,2.126-12.274,7.624c-6.776,29.013-32.314,49.279-62.104,49.279c-5.651,0-10.22,4.578-10.22,10.22c0,5.641,4.568,10.22,10.22,10.22c39.335,0,73.06-26.755,82.002-65.068C338.85,314.608,335.437,309.11,329.939,307.833z"/>
    <path d="M509.803,313.811c-3.495-4.435-9.913-5.192-14.348-1.696l-15.851,12.488l-45.027,9.596l-48.4-11.047l1.666-7.113h52.426c3.24,0,6.295-1.533,8.217-4.149l34.205-46.274c1.308-1.758,2.003-3.883,2.003-6.07v-22.023c0-5.641-4.578-10.22-10.22-10.22c-5.641,0-10.22,4.578-10.22,10.22v18.651L435.118,295.6h-42.513l1.155-4.936c2.8-11.988,0.072-24.231-7.133-33.725c10.823-6.949,22.248-17.966,30.373-33.449c13.418-25.58,13.51-57.577,1.727-72.998c6.05-20.184-0.02-47.93-9.208-67.112c-11.18-23.321-31.425-39.499-51.578-41.216c-3.955-0.337-7.736,1.635-9.719,5.069s-1.799,7.706,0.47,10.966c2.422,3.454,4.589,7.174,6.438,11.037c4.578,9.545,7.072,19.662,6.857,27.746c-0.153,6.142-1.891,9.729-3.434,10.465c-3.485,1.666-15.595-4.681-25.385-22.289c-2.269-4.078-7-6.101-11.517-4.916c-4.517,1.175-7.654,5.263-7.634,9.923c0.051,11.579,2.943,23.669,8.36,34.971c9.279,19.346,28.155,37.485,46.847,45.344c-3.516,5.764-7.399,12.744-12.059,21.625c-4.721,8.993-9.658,18.784-13.377,27.961l-1.523-1.257c-12.948-10.7-27.399-18.641-42.585-23.832l2.861-13.612c1.155-5.519-2.381-10.935-7.9-12.1c-5.529-1.155-10.945,2.381-12.11,7.91l-2.657,12.662c-16.065-2.902-32.56-2.902-48.635,0l-2.657-12.662c-1.155-5.529-6.581-9.065-12.1-7.91c-5.529,1.165-9.065,6.581-7.9,12.1l2.851,13.612c-15.176,5.192-29.637,13.132-42.585,23.832l-1.513,1.257c-3.72-9.167-8.656-18.968-13.367-27.961c-4.66-8.881-8.554-15.861-12.069-21.635c18.692-7.859,37.567-25.988,46.847-45.334c5.416-11.323,8.309-23.413,8.36-34.971c0.02-4.66-3.117-8.748-7.634-9.923c-4.507-1.185-9.249,0.838-11.517,4.916c-9.79,17.608-21.911,23.965-25.396,22.289c-1.543-0.736-3.27-4.323-3.434-10.465c-0.215-8.084,2.289-18.191,6.868-27.756c1.85-3.863,4.006-7.563,6.428-11.017c2.279-3.25,2.463-7.522,0.491-10.966c-1.983-3.434-5.784-5.427-9.729-5.079c-20.163,1.717-40.408,17.895-51.578,41.216c-9.187,19.182-15.258,46.939-9.218,67.132c-11.783,15.421-11.691,47.409,1.727,72.978c8.114,15.483,19.55,26.51,30.362,33.459c-7.195,9.494-9.913,21.727-7.123,33.714l1.155,4.936H76.881l-29.136-39.427v-18.651c0-5.641-4.578-10.22-10.22-10.22c-5.641,0-10.22,4.578-10.22,10.22v22.023c0,2.187,0.695,4.313,2.003,6.07l34.205,46.274c1.921,2.616,4.977,4.149,8.217,4.149h51.548l1.666,7.143l-47.542,11.017l-45.007-9.596l-15.851-12.488c-4.435-3.495-10.853-2.739-14.348,1.696c-3.495,4.435-2.739,10.853,1.696,14.348l17.711,13.97c1.226,0.971,2.667,1.645,4.2,1.972l49.565,10.557c0.705,0.153,1.41,0.225,2.126,0.225c0.777,0,1.553-0.092,2.31-0.266l50.383-11.671c0.583,1.737,1.196,3.454,1.85,5.161l-52.621,24.159c-1.39,0.634-2.606,1.574-3.587,2.739L41.082,416.61c-1.012,1.216-1.737,2.657-2.105,4.2l-4.088,17.343c-1.288,5.498,2.115,10.996,7.603,12.294c0.787,0.184,1.584,0.276,2.361,0.276c4.629,0,8.83-3.178,9.933-7.879l3.536-14.992l31.711-37.966l50.75-23.301c0.879,1.533,1.788,3.045,2.729,4.537l-30.792,26.888c-1.012,0.889-1.84,1.962-2.442,3.168l-17.711,35.861c-0.695,1.41-1.063,2.953-1.063,4.527v18.089c0,5.641,4.578,10.22,10.22,10.22c5.651,0,10.22-4.578,10.22-10.22v-15.707l15.759-31.885l28.012-24.455c5.324,6.152,11.221,11.855,17.639,16.944c23.29,18.477,52.478,28.656,82.206,28.656c29.719,0,58.916-10.179,82.196-28.656c6.581-5.212,12.591-11.058,18.027-17.394l28.513,24.905l15.759,31.885v15.707c0,5.641,4.568,10.22,10.22,10.22c5.641,0,10.22-4.578,10.22-10.22v-18.089c0-1.574-0.368-3.117-1.063-4.527l-17.711-35.861c-0.603-1.206-1.431-2.279-2.443-3.168l-31.364-27.399c0.889-1.431,1.758-2.882,2.596-4.354l51.456,23.628l31.711,37.966l3.536,14.992c1.104,4.711,5.304,7.879,9.933,7.879c0.777,0,1.574-0.092,2.361-0.276c5.488-1.298,8.891-6.796,7.603-12.294l-4.088-17.343c-0.368-1.543-1.094-2.984-2.105-4.2l-34.747-41.604c-0.981-1.165-2.197-2.105-3.587-2.739l-53.367-24.507c0.603-1.594,1.175-3.209,1.717-4.834l51.292,11.701c0.746,0.174,1.513,0.255,2.279,0.255c0.715,0,1.421-0.072,2.126-0.225l49.565-10.557c1.533-0.327,2.974-1.002,4.2-1.972l17.711-13.97C512.542,324.664,513.298,318.246,509.803,313.811z"/>
  </svg>
);

const WhaleIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor" stroke="currentColor" strokeWidth="1.5">
    <path d="M2 12c0 5.523 4.477 10 10 10s10-4.477 10-10S17.523 2 12 2 2 6.477 2 12z"/>
    <path d="M8 12h8"/>
    <path d="M12 8v8"/>
    <path d="M16 8c-2 0-4 2-4 4s2 4 4 4"/>
    <path d="M8 8c2 0 4 2 4 4s-2 4-4 4"/>
  </svg>
);

interface MarketData {
  symbol: string;
  price: number;
  volume_24h: number;
  change_24h: number;
  market_cap?: number;
  high_price_24h?: number;
  low_price_24h?: number;
  funding_rate?: number;
  open_interest?: number;
}

interface TickerData {
  symbol: string;
  price: number;
  priceChangePercent: number;
}

interface HeaderProps {
  tradingMode: TradingMode;
  setTradingMode: (mode: TradingMode) => void;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  marketData?: MarketData;
  usdtBalance: number;
  btcBalance: number;
  totalPortfolioValue: number;
  user: SupabaseUser;
  signOut: () => void;
  marketDataList: MarketData[];
  tickers: Record<string, TickerData>;
  userStatus: UserStatus;
}

// Map user status to icon and color
const userStatusConfig: Record<UserStatus, { icon: React.ReactNode, color: string, label: string }> = {
  'No-Coiner': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>, 
    color: 'text-gray-400', 
    label: 'No-Coiner' 
  },
  'Shrimp': { 
    icon: <ShrimpIcon size={16} className="w-4 h-4" />, 
    color: 'text-pink-400', 
    label: 'Shrimp' 
  },
  'Crab': { 
    icon: <CrabIcon size={16} className="w-4 h-4" />, 
    color: 'text-red-400', 
    label: 'Crab' 
  },
  'Octopus': { 
    icon: <OctopusIcon size={16} className="w-4 h-4" />, 
    color: 'text-purple-400', 
    label: 'Octopus' 
  },
  'Dolphin': { 
    icon: <DolphinIcon size={16} className="w-4 h-4" />, 
    color: 'text-blue-400', 
    label: 'Dolphin' 
  },
  'Shark': { 
    icon: <SharkIcon size={16} className="w-4 h-4" />, 
    color: 'text-green-400', 
    label: 'Shark' 
  },
  'Whale': { 
    icon: <WhaleIcon size={16} className="w-4 h-4" />, 
    color: 'text-indigo-400', 
    label: 'Whale' 
  },
  'Humpback': { 
    icon: <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path></svg>, 
    color: 'text-purple-600', 
    label: 'Humpback' 
  }
};

// Instrument type icons
const instrumentTypeIcons: Record<string, React.ReactNode> = {
  'crypto': <Bitcoin size={16} className="text-orange-400" />,
  'forex': <DollarSign size={16} className="text-green-400" />,
  'commodity': <Gem size={16} className="text-amber-400" />,
  'stock': <BarChart size={16} className="text-blue-400" />,
  'index': <BarChart2 size={16} className="text-purple-400" />,
  'other': <Briefcase size={16} className="text-gray-400" />
};

// Mapping of crypto symbols to full names
const cryptoNames: Record<string, string> = {
  'BTC': 'Bitcoin',
  'ETH': 'Ethereum',
  'BNB': 'Binance Coin',
  'SOL': 'Solana',
  'XRP': 'Ripple',
  'ADA': 'Cardano',
  'DOGE': 'Dogecoin',
  'MATIC': 'Polygon',
  'DOT': 'Polkadot',
  'AVAX': 'Avalanche',
  'LINK': 'Chainlink',
  'UNI': 'Uniswap',
  'SHIB': 'Shiba Inu',
  'LTC': 'Litecoin',
  'ATOM': 'Cosmos',
  'XLM': 'Stellar',
  'NEAR': 'Near Protocol',
  'ALGO': 'Algorand',
  'FTM': 'Fantom',
  'HBAR': 'Hedera',
  'VET': 'VeChain',
  'MANA': 'Decentraland',
  'SAND': 'The Sandbox',
  'AXS': 'Axie Infinity',
  'APE': 'ApeCoin',
  'FIL': 'Filecoin',
  'EOS': 'EOS',
  'EGLD': 'Elrond',
  'XTZ': 'Tezos',
  'AAVE': 'Aave',
  'MKR': 'Maker',
  'COMP': 'Compound',
  'SUSHI': 'SushiSwap',
  'CRV': 'Curve DAO',
  'BAT': 'Basic Attention Token',
  'ZEC': 'Zcash',
  'DASH': 'Dash',
  'XMR': 'Monero',
  'ENJ': 'Enjin Coin',
  'CHZ': 'Chiliz',
  'ONE': 'Harmony',
  'HOT': 'Holo',
  'ZIL': 'Zilliqa',
  'IOTA': 'IOTA',
  'XEM': 'NEM',
  'QTUM': 'Qtum',
  'ICX': 'ICON',
  'OMG': 'OMG Network',
  'ZRX': 'Ox',
  'KSM': 'Kusama',
  'WAVES': 'Waves',
  'NEO': 'NEO',
  'KAVA': 'Kava',
  'ONT': 'Ontology',
  'CELO': 'Celo',
  'THETA': 'Theta Network',
  'LUNA': 'Terra',
  'CAKE': 'PancakeSwap',
  'FARM': 'Harvest Finance',
  'TRX': 'TRON',
  'DCR': 'Decred',
  'HYPE': 'Hypersign',
  'BCH': 'Bitcoin Cash',
  'SUI': 'Sui',
  'TON': 'Toncoin',
};

// Comprehensive list of forex pairs
const allForexPairs: Record<string, string> = {
  'EURUSD': 'Euro / US Dollar', 'GBPUSD': 'British Pound / US Dollar', 'USDJPY': 'US Dollar / Japanese Yen',
  'AUDUSD': 'Australian Dollar / US Dollar', 'USDCAD': 'US Dollar / Canadian Dollar', 'USDCHF': 'US Dollar / Swiss Franc',
  'NZDUSD': 'New Zealand Dollar / US Dollar', 'EURJPY': 'Euro / Japanese Yen', 'GBPJPY': 'British Pound / Japanese Yen',
  'EURGBP': 'Euro / British Pound', 'USDAED': 'US Dollar / UAE Dirham', 'USDAFN': 'US Dollar / Afghan Afghani',
  'USDALL': 'US Dollar / Albanian Lek', 'USDAMD': 'US Dollar / Armenian Dram', 'USDANG': 'US Dollar / Netherlands Antillean Guilder',
  'USDAOA': 'US Dollar / Angolan Kwanza', 'USDARS': 'US Dollar / Argentine Peso', 'USDAWG': 'US Dollar / Aruban Florin',
  'USDAZN': 'US Dollar / Azerbaijani Manat', 'USDBAM': 'US Dollar / Bosnia-Herzegovina Convertible Mark', 'USDBBD': 'US Dollar / Barbadian Dollar',
  'USDBDT': 'US Dollar / Bangladeshi Taka', 'USDBGN': 'US Dollar / Bulgarian Lev', 'USDBHD': 'US Dollar / Bahraini Dinar',
  'USDBIF': 'US Dollar / Burundian Franc', 'USDBMD': 'US Dollar / Bermudan Dollar', 'USDBND': 'US Dollar / Brunei Dollar',
  'USDBOB': 'US Dollar / Bolivian Boliviano', 'USDBRL': 'US Dollar / Brazilian Real', 'USDBSD': 'US Dollar / Bahamian Dollar',
  'USDBTN': 'US Dollar / Bhutanese Ngultrum', 'USDBWP': 'US Dollar / Botswanan Pula', 'USDBYN': 'US Dollar / Belarusian Ruble',
  'USDBYR': 'US Dollar / Old Belarusian Ruble', 'USDBZD': 'US Dollar / Belize Dollar', 'USDCDF': 'US Dollar / Congolese Franc',
  'USDCLP': 'US Dollar / Chilean Peso', 'USDCNY': 'US Dollar / Chinese Yuan', 'USDCNH': 'US Dollar / Offshore Chinese Yuan',
  'USDCOP': 'US Dollar / Colombian Peso', 'USDCRC': 'US Dollar / Costa Rican Colón', 'USDCUC': 'US Dollar / Cuban Convertible Peso',
  'USDCUP': 'US Dollar / Cuban Peso', 'USDCVE': 'US Dollar / Cape Verdean Escudo', 'USDCZK': 'US Dollar / Czech Koruna',
  'USDDJF': 'US Dollar / Djiboutian Franc', 'USDDKK': 'US Dollar / Danish Krone', 'USDDOP': 'US Dollar / Dominican Peso',
  'USDDZD': 'US Dollar / Algerian Dinar', 'USDEGP': 'US Dollar / Egyptian Pound', 'USDERN': 'US Dollar / Eritrean Nakfa',
  'USDETB': 'US Dollar / Ethiopian Birr', 'USDFJD': 'US Dollar / Fijian Dollar', 'USDFKP': 'US Dollar / Falkland Islands Pound',
  'USDGEL': 'US Dollar / Georgian Lari', 'USDGGP': 'US Dollar / Guernsey Pound', 'USDGHS': 'US Dollar / Ghanaian Cedi',
  'USDGIP': 'US Dollar / Gibraltar Pound', 'USDGMD': 'US Dollar / Gambian Dalasi', 'USDGNF': 'US Dollar / Guinean Franc',
  'USDGTQ': 'US Dollar / Guatemalan Quetzal', 'USDGYD': 'US Dollar / Guyanese Dollar', 'USDHKD': 'US Dollar / Hong Kong Dollar',
  'USDHNL': 'US Dollar / Honduran Lempira', 'USDHRK': 'US Dollar / Croatian Kuna', 'USDHTG': 'US Dollar / Haitian Gourde',
  'USDHUF': 'US Dollar / Hungarian Forint', 'USDIDR': 'US Dollar / Indonesian Rupiah', 'USDILS': 'US Dollar / Israeli New Shekel',
  'USDIMP': 'US Dollar / Isle of Man Pound', 'USDINR': 'US Dollar / Indian Rupee', 'USDIQD': 'US Dollar / Iraqi Dinar',
  'USDIRR': 'US Dollar / Iranian Rial', 'USDISK': 'US Dollar / Icelandic Króna', 'USDJEP': 'US Dollar / Jersey Pound',
  'USDJMD': 'US Dollar / Jamaican Dollar', 'USDJOD': 'US Dollar / Jordanian Dinar', 'USDKES': 'US Dollar / Kenyan Shilling',
  'USDKGS': 'US Dollar / Kyrgyzstani Som', 'USDKHR': 'US Dollar / Cambodian Riel', 'USDKMF': 'US Dollar / Comorian Franc',
  'USDKPW': 'US Dollar / North Korean Won', 'USDKRW': 'US Dollar / South Korean Won', 'USDKWD': 'US Dollar / Kuwaiti Dinar',
  'USDKYD': 'US Dollar / Cayman Islands Dollar', 'USDKZT': 'US Dollar / Kazakhstani Tenge', 'USDLAK': 'US Dollar / Lao Kip',
  'USDLBP': 'US Dollar / Lebanese Pound', 'USDLKR': 'US Dollar / Sri Lankan Rupee', 'USDLRD': 'US Dollar / Liberian Dollar',
  'USDLSL': 'US Dollar / Lesotho Loti', 'USDLTL': 'US Dollar / Lithuanian Litas', 'USDLVL': 'US Dollar / Latvian Lats',
  'USDLYD': 'US Dollar / Libyan Dinar', 'USDMAD': 'US Dollar / Moroccan Dirham', 'USDMDL': 'US Dollar / Moldovan Leu',
  'USDMGA': 'US Dollar / Malagasy Ariary', 'USDMKD': 'US Dollar / Macedonian Denar', 'USDMMK': 'US Dollar / Myanmar Kyat',
  'USDMNT': 'US Dollar / Mongolian Tögrög', 'USDMOP': 'US Dollar / Macanese Pataca', 'USDMRU': 'US Dollar / Mauritanian Ouguiya',
  'USDMUR': 'US Dollar / Mauritian Rupee', 'USDMVR': 'US Dollar / Maldivian Rufiyaa', 'USDMWK': 'US Dollar / Malawian Kwacha',
  'USDMXN': 'US Dollar / Mexican Peso', 'USDMYR': 'US Dollar / Malaysian Ringgit', 'USDMZN': 'US Dollar / Mozambican Metical',
  'USDNAD': 'US Dollar / Namibian Dollar', 'USDNGN': 'US Dollar / Nigerian Naira', 'USDNIO': 'US Dollar / Nicaraguan Córdoba',
  'USDNOK': 'US Dollar / Norwegian Krone', 'USDNPR': 'US Dollar / Nepalese Rupee', 'USDOMR': 'US Dollar / Omani Riyal',
  'USDPAB': 'US Dollar / Panamanian Balboa', 'USDPEN': 'US Dollar / Peruvian Sol', 'USDPGK': 'US Dollar / Papua New Guinean Kina',
  'USDPHP': 'US Dollar / Philippine Peso', 'USDPKR': 'US Dollar / Pakistani Rupee', 'USDPLN': 'US Dollar / Polish Złoty',
  'USDPYG': 'US Dollar / Paraguayan Guarani', 'USDQAR': 'US Dollar / Qatari Riyal', 'USDRON': 'US Dollar / Romanian Leu',
  'USDRSD': 'US Dollar / Serbian Dinar', 'USDRUB': 'US Dollar / Russian Ruble', 'USDRWF': 'US Dollar / Rwandan Franc',
  'USDSAR': 'US Dollar / Saudi Riyal', 'USDSBD': 'US Dollar / Solomon Islands Dollar', 'USDSCR': 'US Dollar / Seychellois Rupee',
  'USDSDG': 'US Dollar / Sudanese Pound', 'USDSEK': 'US Dollar / Swedish Krona', 'USDSGD': 'US Dollar / Singapore Dollar',
  'USDSHP': 'US Dollar / Saint Helena Pound', 'USDSLE': 'US Dollar / Sierra Leonean Leone', 'USDSLL': 'US Dollar / Sierra Leonean Leone (old)',
  'USDSOS': 'US Dollar / Somali Shilling', 'USDSRD': 'US Dollar / Surinamese Dollar', 'USDSTD': 'US Dollar / São Tomé and Príncipe Dobra (old)',
  'USDSTN': 'US Dollar / São Tomé and Príncipe Dobra', 'USDSVC': 'US Dollar / Salvadoran Colón', 'USDSYP': 'US Dollar / Syrian Pound',
  'USDSZL': 'US Dollar / Swazi Lilangeni', 'USDTHB': 'US Dollar / Thai Baht', 'USDTJS': 'US Dollar / Tajikistani Somoni',
  'USDTMT': 'US Dollar / Turkmenistani Manat', 'USDTND': 'US Dollar / Tunisian Dinar', 'USDTOP': 'US Dollar / Tongan Paʻanga',
  'USDTRY': 'US Dollar / Turkish Lira', 'USDTTD': 'US Dollar / Trinidad and Tobago Dollar', 'USDTWD': 'US Dollar / New Taiwan Dollar',
  'USDTZS': 'US Dollar / Tanzanian Shilling', 'USDUAH': 'US Dollar / Ukrainian Hryvnia', 'USDUGX': 'US Dollar / Ugandan Shilling',
  'USDUYU': 'US Dollar / Uruguayan Peso', 'USDUZS': 'US Dollar / Uzbekistani Som', 'USDVES': 'US Dollar / Venezuelan Bolívar Soberano',
  'USDVND': 'US Dollar / Vietnamese Đồng', 'USDVUV': 'US Dollar / Vanuatu Vatu', 'USDWST': 'US Dollar / Samoan Tala',
  'USDXAF': 'US Dollar / Central African CFA Franc', 'USDXCD': 'US Dollar / East Caribbean Dollar', 'USDXCG': 'US Dollar / Congolese Franc (old)',
  'USDXDR': 'US Dollar / Special Drawing Rights', 'USDXOF': 'US Dollar / West African CFA Franc', 'USDXPF': 'US Dollar / CFP Franc',
  'USDYER': 'US Dollar / Yemeni Rial', 'USDZAR': 'US Dollar / South African Rand', 'USDZMK': 'US Dollar / Zambian Kwacha (old)',
  'USDZMW': 'US Dollar / Zambian Kwacha', 'USDZWL': 'US Dollar / Zimbabwean Dollar'
};

// Forex pair names
const forexNames: Record<string, string> = allForexPairs;

// Commodity names
const commodityNames: Record<string, string> = {
  'XAUUSD': 'Gold',
  'XAGUSD': 'Silver',
  'XPTUSD': 'Platinum',
  'XPDUSD': 'Palladium',
  'WTIUSD': 'Crude Oil WTI',
  'BRTUSD': 'Brent Crude Oil',
  'NGUSD': 'Natural Gas'
};

const Header: React.FC<HeaderProps> = ({ 
  tradingMode, 
  setTradingMode, 
  selectedPair, 
  setSelectedPair, 
  currentPrice, 
  marketData, 
  usdtBalance, 
  btcBalance, 
  totalPortfolioValue, 
  user, 
  signOut,
  marketDataList,
  tickers,
  userStatus
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPairDropdownOpen, setIsPairDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDisplayCurrency, setSelectedDisplayCurrency] = useState<string>(() => {
    // Initialize from localStorage or default to USD
    return localStorage.getItem('selectedDisplayCurrency') || 'USD';
  });
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);
  
  const userMenuRef = useRef<HTMLDivElement>(null);
  const pairDropdownRef = useRef<HTMLDivElement>(null);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const navDropdownRef = useRef<HTMLDivElement>(null);
  
  // Check if user is being impersonated
  useEffect(() => {
    const impersonationFlag = localStorage.getItem('is_impersonating');
    setIsImpersonating(impersonationFlag === 'true');
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (pairDropdownRef.current && !pairDropdownRef.current.contains(event.target as Node)) {
        setIsPairDropdownOpen(false);
      }
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setIsCurrencyDropdownOpen(false);
      }
      if (navDropdownRef.current && !navDropdownRef.current.contains(event.target as Node)) {
        setIsNavDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Persist selected display currency to localStorage
  useEffect(() => {
    localStorage.setItem('selectedDisplayCurrency', selectedDisplayCurrency);
  }, [selectedDisplayCurrency]);

  // Currency options for the dropdown
  const currencyOptions = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
    { code: 'BTC', symbol: '₿', name: 'Bitcoin' },
    { code: 'ETH', symbol: 'Ξ', name: 'Ethereum' },
  ];

  // Function to get exchange rate from USD to target currency
  const getExchangeRate = useCallback((targetCurrencyCode: string, marketDataList: MarketData[]): number => {
    if (targetCurrencyCode === 'USD') {
      return 1; // No conversion needed for USD
    }

    // Crypto conversions (USD to Crypto)
    if (targetCurrencyCode === 'BTC') {
      const btcUsdRate = marketDataList.find(data => data.symbol === 'BTCUSDT')?.price;
      return btcUsdRate && btcUsdRate > 0 ? 1 / btcUsdRate : 0;
    }
    if (targetCurrencyCode === 'ETH') {
      const ethUsdRate = marketDataList.find(data => data.symbol === 'ETHUSDT')?.price;
      return ethUsdRate && ethUsdRate > 0 ? 1 / ethUsdRate : 0;
    }

    // Fiat conversions (USD to Fiat)
    // EUR/USD: 1 EUR = X USD -> 1 USD = 1/X EUR
    const eurUsdRate = marketDataList.find(data => data.symbol === 'EURUSD')?.price;
    if (targetCurrencyCode === 'EUR' && eurUsdRate && eurUsdRate > 0) {
      return 1 / eurUsdRate;
    }

    // GBP/USD: 1 GBP = X USD -> 1 USD = 1/X GBP
    const gbpUsdRate = marketDataList.find(data => data.symbol === 'GBPUSD')?.price;
    if (targetCurrencyCode === 'GBP' && gbpUsdRate && gbpUsdRate > 0) {
      return 1 / gbpUsdRate;
    }

    // USD/CHF: 1 USD = X CHF
    const usdChfRate = marketDataList.find(data => data.symbol === 'USDCHF')?.price;
    if (targetCurrencyCode === 'CHF' && usdChfRate && usdChfRate > 0) {
      return usdChfRate;
    }

    // USD/CAD: 1 USD = X CAD
    const usdCadRate = marketDataList.find(data => data.symbol === 'USDCAD')?.price;
    if (targetCurrencyCode === 'CAD' && usdCadRate && usdCadRate > 0) {
      return usdCadRate;
    }

    // AUD/USD: 1 AUD = X USD -> 1 USD = 1/X AUD
    const audUsdRate = marketDataList.find(data => data.symbol === 'AUDUSD')?.price;
    if (targetCurrencyCode === 'AUD' && audUsdRate && audUsdRate > 0) {
      return 1 / audUsdRate;
    }

    // USD/HKD: 1 USD = X HKD
    const usdHkdRate = marketDataList.find(data => data.symbol === 'USDHKD')?.price;
    if (targetCurrencyCode === 'HKD' && usdHkdRate && usdHkdRate > 0) {
      return usdHkdRate;
    }

    // NZD/USD: 1 NZD = X USD -> 1 USD = 1/X NZD
    const nzdUsdRate = marketDataList.find(data => data.symbol === 'NZDUSD')?.price;
    if (targetCurrencyCode === 'NZD' && nzdUsdRate && nzdUsdRate > 0) {
      return 1 / nzdUsdRate;
    }

    // USD/SEK: 1 USD = X SEK
    const usdSekRate = marketDataList.find(data => data.symbol === 'USDSEK')?.price;
    if (targetCurrencyCode === 'SEK' && usdSekRate && usdSekRate > 0) {
      return usdSekRate;
    }

    // USD/NOK: 1 USD = X NOK
    const usdNokRate = marketDataList.find(data => data.symbol === 'USDNOK')?.price;
    if (targetCurrencyCode === 'NOK' && usdNokRate && usdNokRate > 0) {
      return usdNokRate;
    }

    // USD/SGD: 1 USD = X SGD
    const usdSgdRate = marketDataList.find(data => data.symbol === 'USDSGD')?.price;
    if (targetCurrencyCode === 'SGD' && usdSgdRate && usdSgdRate > 0) {
      return usdSgdRate;
    }

    // USD/INR: 1 USD = X INR
    const usdInrRate = marketDataList.find(data => data.symbol === 'USDINR')?.price;
    if (targetCurrencyCode === 'INR' && usdInrRate && usdInrRate > 0) {
      return usdInrRate;
    }

    // USD/ZAR: 1 USD = X ZAR
    const usdZarRate = marketDataList.find(data => data.symbol === 'USDZAR')?.price;
    if (targetCurrencyCode === 'ZAR' && usdZarRate && usdZarRate > 0) {
      return usdZarRate;
    }

    // USD/TRY: 1 USD = X TRY
    const usdTryRate = marketDataList.find(data => data.symbol === 'USDTRY')?.price;
    if (targetCurrencyCode === 'TRY' && usdTryRate && usdTryRate > 0) {
      return usdTryRate;
    }

    // USD/BRL: 1 USD = X BRL
    const usdBrlRate = marketDataList.find(data => data.symbol === 'USDBRL')?.price;
    if (targetCurrencyCode === 'BRL' && usdBrlRate && usdBrlRate > 0) {
      return usdBrlRate;
    }

    // USD/MXN: 1 USD = X MXN
    const usdMxnRate = marketDataList.find(data => data.symbol === 'USDMXN')?.price;
    if (targetCurrencyCode === 'MXN' && usdMxnRate && usdMxnRate > 0) {
      return usdMxnRate;
    }

    // USD/JPY: 1 USD = X JPY
    const usdJpyRate = marketDataList.find(data => data.symbol === 'USDJPY')?.price;
    if (targetCurrencyCode === 'JPY' && usdJpyRate && usdJpyRate > 0) {
      return usdJpyRate;
    }

    // USD/CNY: 1 USD = X CNY
    const usdCnyRate = marketDataList.find(data => data.symbol === 'USDCNY')?.price;
    if (targetCurrencyCode === 'CNY' && usdCnyRate && usdCnyRate > 0) {
      return usdCnyRate;
    }

    // USD/RUB: 1 USD = X RUB
    const usdRubRate = marketDataList.find(data => data.symbol === 'USDRUB')?.price;
    if (targetCurrencyCode === 'RUB' && usdRubRate && usdRubRate > 0) {
      return usdRubRate;
    }

    // USD/KRW: 1 USD = X KRW
    const usdKrwRate = marketDataList.find(data => data.symbol === 'USDKRW')?.price;
    if (targetCurrencyCode === 'KRW' && usdKrwRate && usdKrwRate > 0) {
      return usdKrwRate;
    }

    // Default to 1 if rate not found (no conversion)
    return 1;
  }, []);

  // Calculate converted portfolio value
  const convertedPortfolioValue = useMemo(() => {
    const rate = getExchangeRate(selectedDisplayCurrency, marketDataList);
    return totalPortfolioValue * rate;
  }, [totalPortfolioValue, selectedDisplayCurrency, marketDataList, getExchangeRate]);

  // Get selected currency symbol
  const selectedCurrencySymbol = useMemo(() => {
    const option = currencyOptions.find(opt => opt.code === selectedDisplayCurrency);
    return option ? option.symbol : '$';
  }, [selectedDisplayCurrency]);
  
  // Format price with appropriate precision
  const formatPrice = (price: number) => {
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  };
  
  // Get price change color
  const getPriceChangeColor = (change: number) => {
    return change >= 0 ? 'text-green-500' : 'text-red-500';
  };
  
  // Get price change icon
  const getPriceChangeIcon = (change: number) => {
    return change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />;
  };

  // Get status config for current user
  const statusConfig = userStatusConfig[userStatus];

  // Format pair for display
  const formatPairForDisplay = (pair: string) => {
    // For forex pairs like EURUSD
    if (pair.length === 6 && !pair.includes('USDT')) {
      return `${pair.substring(0, 3)}/${pair.substring(3, 6)}`;
    }
    // For crypto pairs like BTCUSDT
    if (pair.endsWith('USDT')) {
      return `${pair.replace('USDT', '')}/${pair.substring(pair.length - 4)}`;
    }
    // For commodity pairs like XAUUSD
    if (pair.startsWith('XAU') || pair.startsWith('XAG') || pair.startsWith('XPT')) {
      return `${pair.substring(0, 3)}/${pair.substring(3)}`;
    }
    return pair;
  };

  // Get full name for a trading pair
  const getPairFullName = (symbol: string): string => {
    // For crypto pairs
    if (symbol.endsWith('USDT')) {
      const base = symbol.replace('USDT', '');
      return cryptoNames[base] || base;
    }
    
    // For forex pairs
    if (forexNames[symbol]) {
      return forexNames[symbol];
    }
    
    // For commodity pairs
    if (commodityNames[symbol]) {
      return commodityNames[symbol];
    }
    
    return symbol;
  };

  // Determine instrument type based on symbol
  const getInstrumentType = (symbol: string): string => {
    // Crypto pairs
    if (symbol.endsWith('USDT')) {
      return 'crypto';
    }
    
    // Forex pairs
    if (allForexPairs[symbol]) {
      return 'forex';
    }
    
    // Commodities
    if (symbol.startsWith('XAU') || symbol.startsWith('XAG') || symbol.startsWith('XPT') || 
        symbol.startsWith('XPD') || symbol === 'WTIUSD' || symbol === 'BRTUSD' || 
        symbol === 'NGUSD' || symbol === 'ALUUSD' || symbol === 'COPPERUSD' || symbol === 'ZINCUSD') {
      return 'commodity';
    }
    
    // Stocks
    const stockSymbols = ['AAPL', 'TSLA', 'AMZN', 'MSFT', 'NVDA', 'META', 'NFLX', 'GOOGL', 'BA', 'JPM'];
    if (stockSymbols.includes(symbol)) {
      return 'stock';
    }
    
    // Indices
    if (symbol.includes('SPX') || symbol.includes('NDX') || symbol.includes('DJI')) {
      return 'index';
    }
    
    return 'other';
  };

  // Filter and sort market data for dropdown
  const getFilteredAndSortedMarketData = useMemo(() => {
    // First, filter based on trading mode
    let filteredData = [...marketDataList];
    
    if (tradingMode === 'futures') {
      filteredData = filteredData.filter(item => item.symbol.endsWith('USDT'));
    } else if (tradingMode === 'cfd') {
      filteredData = filteredData.filter(item => {
        const type = getInstrumentType(item.symbol);
        return type === 'forex' || type === 'commodity' || type === 'stock' || type === 'index';
      });
    }
    
    // Then apply search filter if there's a search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filteredData = filteredData.filter(item => {
        // Check if symbol matches
        if (item.symbol.toLowerCase().includes(term)) {
          return true;
        }
        
        // Check if formatted pair matches
        if (formatPairForDisplay(item.symbol).toLowerCase().includes(term)) {
          return true;
        }
        
        // Check if full name matches
        const fullName = getPairFullName(item.symbol).toLowerCase();
        if (fullName.includes(term)) {
          return true;
        }
        
        // For crypto pairs, check if the base currency name matches
        if (item.symbol.endsWith('USDT')) {
          const base = item.symbol.replace('USDT', '');
          const baseName = cryptoNames[base]?.toLowerCase() || '';
          if (baseName.includes(term)) {
            return true;
          }
        }
        
        return false;
      });
    }
    
    // Add instrument type to each item
    const dataWithType = filteredData.map(item => ({
      ...item,
      instrumentType: getInstrumentType(item.symbol)
    }));
    
    // Sort by market cap (descending) or alphabetically if market cap is not available
    dataWithType.sort((a, b) => {
      // First by market cap (if available)
      if (a.market_cap && b.market_cap) {
        return b.market_cap - a.market_cap;
      } else if (a.market_cap) {
        return -1; // a has market cap, b doesn't
      } else if (b.market_cap) {
        return 1; // b has market cap, a doesn't
      }
      
      // Then by volume (if market cap not available)
      if (a.volume_24h && b.volume_24h) {
        return b.volume_24h - a.volume_24h;
      }
      
      // Finally alphabetically
      return a.symbol.localeCompare(b.symbol);
    });
    
    // Group by instrument type
    const groupedData: Record<string, typeof dataWithType> = {};
    
    dataWithType.forEach(item => {
      if (!groupedData[item.instrumentType]) {
        groupedData[item.instrumentType] = [];
      }
      groupedData[item.instrumentType].push(item);
    });
    
    return groupedData;
  }, [marketDataList, tradingMode, searchTerm]);

  // Get instrument type display name
  const getInstrumentTypeDisplay = (type: string): string => {
    const typeMap: Record<string, string> = {
      'crypto': 'Cryptocurrencies',
      'forex': 'Forex Pairs',
      'commodity': 'Commodities',
      'stock': 'Stocks',
      'index': 'Indices',
      'other': 'Other Markets'
    };
    
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Get real-time price and change from WebSocket ticker for selected pair
  const tickerData = tickers[selectedPair];
  const displayPrice = tickerData?.price || currentPrice || 0;
  const change24h = tickerData?.priceChangePercent || 0;
  
  // Debug WebSocket data
  console.log('[Header] WebSocket ticker data:', {
    selectedPair,
    tickerData,
    hasTickerData: !!tickerData,
    allTickers: tickers,
    priceChangePercent: tickerData?.priceChangePercent
  });

  const handleSignOut = () => {
    // Clear impersonation flag when signing out
    localStorage.removeItem('is_impersonating');
    signOut();
  };

  return (
    <header className="bg-slate-800/80 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 min-w-0">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center" onClick={() => setTradingMode('home')}>
              <div className="flex items-center gap-2 lg:gap-3">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center transform rotate-45">
                    <TrendingUp size={16} className="text-white transform -rotate-45" />
                  </div>
                </div>
                <span className="text-lg lg:text-xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">WhiteRock24</span>
              </div>
            </Link>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-slate-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            <button 
              onClick={() => setTradingMode('home')}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'home' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Home size={14} className="lg:w-4 lg:h-4" />
                <span>{t('navigation.home')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('swap')}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'swap' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Repeat size={14} className="lg:w-4 lg:h-4" />
                <span>{t('trading.swap')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => {
                setTradingMode('futures');
                setSelectedPair('BTCUSDT');
              }}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'futures' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <TrendingUp size={14} className="lg:w-4 lg:h-4" />
                <span>{t('trading.futures')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => {
                setTradingMode('cfd');
                setSelectedPair('EURUSD');
              }}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'cfd' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <BarChart2 size={14} className="lg:w-4 lg:h-4" />
                <span>{t('trading.cfd')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('prop_firm')}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'prop_firm' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Award size={14} className="lg:w-4 lg:h-4" />
                <span>{t('propFirm.prop')} {t('propFirm.firm')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('robot')}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'robot' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Bot size={14} className="lg:w-4 lg:h-4" />
                <span>{t('trading.robot')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('events')}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'events' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Calendar size={14} className="lg:w-4 lg:h-4" />
                <span>{t('trading.events')}</span>
              </div>
            </button>
            
            <button 
              onClick={() => setTradingMode('staking')}
              className={`flex items-center gap-1 px-2 lg:px-3 xl:px-4 py-2 rounded-lg text-xs lg:text-sm xl:text-base font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                tradingMode === 'staking' 
                  ? 'bg-blue-600/20 text-blue-400' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center space-x-1">
                <Landmark size={14} className="lg:w-4 lg:h-4" />
                <span>{t('trading.staking')}</span>
              </div>
            </button>
          </nav>
          
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 flex-shrink-0">
            <div className="hidden md:flex items-center space-x-3">
              <LanguageSwitcher />
              
              {/* Trading Pair Selector */}
              {(tradingMode === 'futures' || tradingMode === 'cfd') && (
                <div className="relative" ref={pairDropdownRef}>
                  <button
                    onClick={() => setIsPairDropdownOpen(!isPairDropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700"
                  >
                    <span className="font-medium">{formatPairForDisplay(selectedPair)}</span>
                    <span className="text-xs text-slate-400">Perp</span>
                    {isPairDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  
                  {isPairDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-80 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50">
                      <div className="p-2 sticky top-0 bg-slate-900 border-b border-slate-700 z-10">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search pairs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 pl-9 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
                        </div>
                      </div>
                      
                      <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                        {Object.entries(getFilteredAndSortedMarketData).map(([type, items]) => (
                          <div key={type}>
                            <div className="px-3 py-1.5 bg-slate-800/50 text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center">
                              {instrumentTypeIcons[type] || <Briefcase size={14} className="mr-1.5" />}
                              <span>{getInstrumentTypeDisplay(type)}</span>
                            </div>
                            
                            {items.map((item) => (
                              <button
                                key={item.symbol}
                                onClick={() => {
                                  setSelectedPair(item.symbol);
                                  setIsPairDropdownOpen(false);
                                }}
                                className={`flex items-center justify-between w-full px-3 py-2.5 text-sm hover:bg-slate-700/50 ${
                                  selectedPair === item.symbol ? 'bg-blue-900/20' : 'text-white'
                                }`}
                              >
                                <div className="flex items-center">
                                  <Bitcoin size={16} className="mr-2" />
                                  <div className="flex flex-col items-start">
                                    <span className="font-medium">{formatPairForDisplay(item.symbol)}</span>
                                    <span className="text-xs text-slate-400">{getPairFullName(item.symbol)}</span>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-800 text-slate-300 mr-2">
                                    CRYPTO
                                  </span>
                                  <span className={`text-xs font-medium ${getPriceChangeColor(item.change_24h)}`}>
                                    {(item.change_24h ?? 0) >= 0 ? '+' : ''}{(item.change_24h ?? 0).toFixed(2)}%
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        ))}
                        
                        {Object.keys(getFilteredAndSortedMarketData).length === 0 && (
                          <div className="px-4 py-3 text-sm text-slate-400 text-center">
                            No matching pairs found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Current Price - FIXED WIDTH ADDED HERE */}
              <div className="hidden lg:block min-w-[150px] text-right">
                <div className="text-xs text-slate-400">{t('header.price')}</div>
                <div className="flex items-center justify-end">
                  <span className="text-white font-semibold">${formatPrice(displayPrice)}</span>
                  {marketData && (
                    <span className={`ml-2 text-xs flex items-center ${getPriceChangeColor(change24h)}`}>
                      {getPriceChangeIcon(change24h)}
                      <span className="ml-1">{change24h.toFixed(2)}%</span>
                    </span>
                  )}
                </div>
              </div>
              
              {/* User Status */}
              <div className="hidden lg:flex items-center px-3 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600/50">
                <div className={`flex items-center ${statusConfig.color}`}>
                  {statusConfig.icon}
                  <span className="ml-1.5 text-sm font-medium">{statusConfig.label}</span>
                </div>
              </div>
              
              {/* Portfolio Value */}
              <div className="relative" ref={currencyDropdownRef}>
                <button
                  onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                  className="hidden lg:flex flex-col items-end px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 min-w-[220px]"
                >
                  <div className="text-xs text-slate-400">{t('header.portfolioValue')}</div>
                  <div className="flex items-center">
                    {selectedDisplayCurrency === 'USD' ? (
                      <span className="text-white font-semibold text-lg">
                        ${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ) : (
                      <>
                        <span className="text-white font-semibold text-lg">
                          {selectedCurrencySymbol}
                          {convertedPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="ml-2 text-slate-400 text-xs">
                          (${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </span>
                      </>
                    )}
                    <ChevronDown size={16} className="ml-1 text-slate-400" />
                  </div>
                </button>
                {isCurrencyDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                    {currencyOptions.map(option => (
                      <button
                        key={option.code}
                        onClick={() => {
                          setSelectedDisplayCurrency(option.code);
                          setIsCurrencyDropdownOpen(false);
                        }}
                        className={`flex items-center w-full px-4 py-2 text-sm hover:bg-slate-700/50 ${selectedDisplayCurrency === option.code ? 'bg-blue-900/20 text-blue-400' : 'text-white'}`}
                      >
                        {option.symbol} {option.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Wallet Button */}
              
              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-slate-300 hover:text-white focus:outline-none"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">
                    {user.email?.charAt(0).toUpperCase()}
                  </div>
                  <ChevronDown size={16} className="text-slate-400" />
                </button>
                
                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                    <div className="p-3 border-b border-slate-700">
                      <p className="text-sm font-medium text-white truncate">{user.email}</p>
                      <div className="flex items-center mt-1">
                        <div className={`flex items-center ${statusConfig.color} text-xs`}>
                          {statusConfig.icon}
                          <span className="ml-1">{statusConfig.label}</span>
                        </div>
                      </div>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setTradingMode('profile');
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                      >
                        <User size={16} className="mr-2" />
                        {t('navigation.profile')}
                      </button>
                      <button
                        onClick={() => {
                          setTradingMode('wallet');
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                      >
                        <Wallet size={16} className="mr-2" />
                        {t('navigation.wallet')}
                      </button>
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                      >
                        <Settings size={16} className="mr-2" />
                        {t('navigation.settings')}
                      </button>
                      <button
                        onClick={() => {
                          handleSignOut();
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                      >
                        <LogOut size={16} className="mr-2" />
                        {t('navigation.signOut')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-center text-sm font-medium">
            <div className="flex items-center justify-center gap-2">
              <span>⚠️ Admin Impersonation Mode Active</span>
              <button
                onClick={() => {
                  localStorage.removeItem('is_impersonating');
                  window.location.href = '/admin'; // Redirect back to admin panel
                }}
                className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs transition-colors"
              >
                Exit Impersonation
              </button>
            </div>
          </div>
        )}
        
        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700">
            <div className="px-2 pt-2 pb-3 space-y-6">
              {/* Language Switcher for Mobile */}
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Language</span>
                <LanguageSwitcher />
              </div>
              
              <div className="border-t border-slate-700/50 pt-4 space-y-1">
                <button
                  onClick={() => {
                    setTradingMode('home');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'home' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Home size={18} className="mr-2" />
                    {t('navigation.home')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('swap');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'swap' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Repeat size={18} className="mr-2" />
                    {t('trading.swap')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('futures');
                    setSelectedPair('BTCUSDT');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'futures' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <TrendingUp size={18} className="mr-2" />
                    {t('trading.futures')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('cfd');
                    setSelectedPair('EURUSD');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'cfd' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <BarChart2 size={18} className="mr-2" />
                    {t('trading.cfd')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('prop_firm');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'prop_firm' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Award size={18} className="mr-2" />
                    {t('propFirm.prop')} {t('propFirm.firm')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('robot');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'robot' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Bot size={18} className="mr-2" />
                    {t('trading.robot')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('events');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'events' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Calendar size={18} className="mr-2" />
                    {t('trading.events')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('staking');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'staking' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Landmark size={18} className="mr-2" />
                    <span>{t('trading.staking')}</span>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('wallet');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'wallet' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Wallet size={18} className="mr-2" />
                    {t('navigation.wallet')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    setTradingMode('profile');
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    tradingMode === 'profile' 
                      ? 'bg-blue-600/20 text-blue-400' 
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <User size={18} className="mr-2" />
                    {t('navigation.profile')}
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    handleSignOut();
                    setIsMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <div className="flex items-center">
                    <LogOut size={18} className="mr-2" />
                    {t('navigation.signOut')}
                  </div>
                </button>
              </div>
            </div>
            
            {/* Mobile Trading Pair Selector */}
            {(tradingMode === 'futures' || tradingMode === 'cfd') && (
              <div className="px-4 py-2 border-t border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Trading Pair</span>
                  <div className="relative">
                    <select
                      value={selectedPair}
                      onChange={(e) => setSelectedPair(e.target.value)}
                      className="bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-1.5 text-sm appearance-none pr-8"
                    >
                      {Object.entries(getFilteredAndSortedMarketData).map(([type, items]) => (
                        <optgroup key={type} label={getInstrumentTypeDisplay(type)}>
                          {items.map((item) => (
                            <option key={item.symbol} value={item.symbol}>
                              {formatPairForDisplay(item.symbol)}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
              </div>
            )}
            
            {/* Mobile User Status and Balance */}
            <div className="px-5 py-4 border-t border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <div className={`flex items-center ${statusConfig.color}`}>
                  {statusConfig.icon}
                  <span className="ml-1.5 text-sm font-medium">{statusConfig.label}</span>
                </div>
                <span className="text-white font-semibold">
                  ${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;